import React, { useState, useEffect } from 'react';
import '../styles/Calendar.css';

// Platform colors
const SOURCE_COLORS = {
  airbnb: '#E24B4A',
  booking: '#378ADD',
  manual: '#F5C033'
};
function sourceColor(source) {
  return SOURCE_COLORS[(source || '').toLowerCase()] || '#999';
}
function sourceLabel(source) {
  const s = (source || '').toLowerCase();
  if (s === 'airbnb') return 'Airbnb';
  if (s === 'booking') return 'Booking';
  if (s === 'manual') return 'Bloqueo manual';
  if (s === 'inquiry') return 'Solicitud';
  return source || '';
}

// Parse a date value as a LOCAL date (avoids UTC off-by-one).
// Accepts "2026-07-23" or "2026-07-23T00:00:00.000Z" — we take just the Y-M-D.
function parseLocalDate(v) {
  if (!v) return null;
  const s = String(v).slice(0, 10); // YYYY-MM-DD
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d); // local midnight
}
function sameDay(a, b) {
  return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function Calendar() {
  const [reservations, setReservations] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [formFields, setFormFields] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    loadCalendarData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  useEffect(() => {
    loadFormFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCalendarData = async () => {
    try {
      setLoading(true);
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = new Date(year, month, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];
      const response = await fetch(`/api/dashboard/calendar?startDate=${startDate}&endDate=${endDate}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReservations(data.reservations || []);
      }
    } catch (error) {
      console.error('Error loading calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFormFields = async () => {
    try {
      const response = await fetch('/api/forms/1/fields', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFormFields(data.fields || []);
      }
    } catch (error) {
      console.error('Error loading form fields:', error);
    }
  };

  const handlePrevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const handleNextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  const setVal = (id, v) => setFormValues({ ...formValues, [id]: v });

  const handleCreateBlock = async (e) => {
    e.preventDefault();
    const checkInField = formFields.find(f => f.fieldType === 'checkin');
    const checkOutField = formFields.find(f => f.fieldType === 'checkout');
    const notesField = formFields.find(f => f.fieldType === 'textarea' && f.isDefault);

    const checkIn = checkInField ? formValues[checkInField.id] : '';
    const checkOut = checkOutField ? formValues[checkOutField.id] : '';
    const notes = notesField ? (formValues[notesField.id] || '') : '';

    if (!checkIn || !checkOut) {
      alert('Check-in and check-out dates are required');
      return;
    }
    for (const f of formFields) {
      if (f.isDefault) continue;
      if (f.isRequired && !String(formValues[f.id] || '').trim()) {
        alert(`"${f.fieldLabel}" is required`);
        return;
      }
    }

    setSaving(true);
    try {
      const fieldValues = formFields
        .filter(f => !f.isDefault && String(formValues[f.id] || '').trim() !== '')
        .map(f => ({ fieldId: f.id, fieldLabel: f.fieldLabel, value: formValues[f.id] }));

      const response = await fetch('/api/reservations/block', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ checkIn, checkOut, notes, fieldValues })
      });

      if (response.ok) {
        setFormValues({});
        setShowBlockForm(false);
        loadCalendarData();
      } else {
        const err = await response.json();
        alert('Error: ' + (err.error || 'Failed to create block'));
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBlock = async (id) => {
    if (!window.confirm('Delete this manual block?')) return;
    try {
      const response = await fetch(`/api/reservations/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) loadCalendarData();
      else { const err = await response.json(); alert('Error: ' + (err.error || 'Failed to delete')); }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  // For a given day, figure out what's happening: which booking is checking in,
  // checking out, or occupying the full night.
  //   checkIn day  -> top half (arrival)
  //   checkOut day -> bottom half (departure)
  //   full night   -> solid
  //   both on same day -> turnover (top = arriving, bottom = departing) + icon
  const dayInfo = (day) => {
    const d = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    let arriving = null;   // reservation checking IN this day
    let departing = null;  // reservation checking OUT this day
    let staying = null;    // reservation occupying the full night (not an edge)

    reservations.forEach(r => {
      const ci = parseLocalDate(r.checkIn);
      const co = parseLocalDate(r.checkOut);
      if (!ci || !co) return;
      if (sameDay(d, ci)) arriving = r;
      if (sameDay(d, co)) departing = r;
      // full night: strictly between check-in and check-out
      if (d > ci && d < co) staying = r;
    });

    return { arriving, departing, staying, isTurnover: !!(arriving && departing) };
  };

  const renderField = (f) => {
    const val = formValues[f.id] || '';
    if (f.fieldType === 'checkin' || f.fieldType === 'checkout' || f.fieldType === 'date') {
      return <input type="date" value={val} onChange={(e) => setVal(f.id, e.target.value)} required={f.isRequired} />;
    }
    if (f.fieldType === 'textarea') {
      return <textarea placeholder={f.placeholder || f.fieldLabel} value={val} onChange={(e) => setVal(f.id, e.target.value)} required={f.isRequired} />;
    }
    if (f.fieldType === 'select') {
      return (
        <select value={val} onChange={(e) => setVal(f.id, e.target.value)} required={f.isRequired}>
          <option value="">Select…</option>
          {(f.options || []).map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
        </select>
      );
    }
    const typeMap = { email: 'email', phone: 'tel', number: 'number', text: 'text' };
    return (
      <input
        type={typeMap[f.fieldType] || 'text'}
        placeholder={f.placeholder || f.fieldLabel}
        value={val}
        onChange={(e) => setVal(f.id, e.target.value)}
        required={f.isRequired}
      />
    );
  };

  const renderDayFill = (info) => {
    const { arriving, departing, staying, isTurnover } = info;

    // Full night — solid band
    if (staying && !arriving && !departing) {
      return <div className="cd-fill cd-solid" style={{ background: sourceColor(staying.source) }} />;
    }

    // Turnover — top arriving, bottom departing, divider + icon
    if (isTurnover) {
      return (
        <div className="cd-fill cd-turnover">
          <div className="cd-half cd-top" style={{ background: sourceColor(arriving.source) }} />
          <div className="cd-half cd-bottom" style={{ background: sourceColor(departing.source) }} />
          <div className="cd-divider" />
          <div className="cd-turn-icon" title={`Limpieza: sale ${sourceLabel(departing.source)}, entra ${sourceLabel(arriving.source)}`}>🧹</div>
        </div>
      );
    }

    // Check-in only — top half
    if (arriving) {
      return (
        <div className="cd-fill">
          <div className="cd-half cd-top" style={{ background: sourceColor(arriving.source) }} />
        </div>
      );
    }

    // Check-out only — bottom half
    if (departing) {
      return (
        <div className="cd-fill">
          <div className="cd-half cd-bottom" style={{ background: sourceColor(departing.source) }} />
        </div>
      );
    }

    return null;
  };

  if (loading) return <div style={{ padding: '20px' }}>Loading calendar...</div>;

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const weeks = [];
  let currentWeek = Array(firstDay).fill(null);
  days.forEach(day => {
    currentWeek.push(day);
    if (currentWeek.length === 7) { weeks.push(currentWeek); currentWeek = []; }
  });
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) currentWeek.push(null);
    weeks.push(currentWeek);
  }

  const manualBlocks = reservations.filter(r => r.source === 'manual');

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <h2>Calendar View</h2>
        <div className="calendar-nav">
          <button onClick={handlePrevMonth}>←</button>
          <h3>{currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
          <button onClick={handleNextMonth}>→</button>
        </div>
      </div>

      <div className="calendar-grid">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="calendar-day-header">{day}</div>
        ))}
        {weeks.map((week, weekIdx) =>
          week.map((day, dayIdx) => {
            const info = day ? dayInfo(day) : null;
            const hasAny = info && (info.arriving || info.departing || info.staying);
            return (
              <div
                key={`${weekIdx}-${dayIdx}`}
                className={`calendar-day ${!day ? 'empty' : hasAny ? 'booked' : ''} ${info && info.isTurnover ? 'is-turnover' : ''}`}
              >
                {day && (
                  <>
                    {renderDayFill(info)}
                    <div className="day-number">{day}</div>
                  </>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="calendar-legend">
        <div><span style={{ background: SOURCE_COLORS.airbnb }}></span> Airbnb</div>
        <div><span style={{ background: SOURCE_COLORS.booking }}></span> Booking.com</div>
        <div><span style={{ background: SOURCE_COLORS.manual }}></span> Manual Block</div>
        <div className="lg-split"><span className="lg-top"></span> Arrival (top)</div>
        <div className="lg-split"><span className="lg-bottom"></span> Departure (bottom)</div>
        <div><span className="lg-turn">🧹</span> Same-day turnover (cleaning)</div>
      </div>

      <div className="block-form-container">
        {!showBlockForm ? (
          <button onClick={() => setShowBlockForm(true)} className="btn-add-block">+ Add Manual Block</button>
        ) : (
          <form onSubmit={handleCreateBlock} className="block-form">
            <h3>Add Manual Block</h3>
            {formFields.map((f) => (
              <div key={f.id} className="bf-custom">
                <label className="bf-label">{f.fieldLabel}{f.isRequired ? ' *' : ''}</label>
                {renderField(f)}
              </div>
            ))}
            <div className="form-actions">
              <button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Block'}</button>
              <button type="button" onClick={() => { setShowBlockForm(false); setFormValues({}); }}>Cancel</button>
            </div>
          </form>
        )}
      </div>

      {manualBlocks.length > 0 && (
        <div className="manual-blocks-list">
          <h3>Manual Blocks This Month</h3>
          {manualBlocks.map((b) => (
            <div key={b.id} className="manual-block-row">
              <span>
                {parseLocalDate(b.checkIn).toLocaleDateString()} → {parseLocalDate(b.checkOut).toLocaleDateString()}
                {b.notes ? ` — ${b.notes}` : ''}
              </span>
              <button className="btn-delete-block" onClick={() => handleDeleteBlock(b.id)}>Delete</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
